import { useEffect, useRef, type MutableRefObject } from "react";
import type { ThreadMessage, ThreadRuntime } from "@assistant-ui/react";

import { useStudioLocale } from "@/i18n";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n";
import { useStudioStore } from "@/store/studio-store";
import {
  readProjectChatThread,
  type ProjectChatThreadTurn,
} from "../project-chat-thread";

// The two Chat shells are mutually exclusive, but ownership must survive the
// build/edit transition so the newly mounted shell can remove the previous
// project's durable projection before hydrating the next one.
export const PROJECT_CHAT_OWNED_MESSAGE_IDS = new Set<string>();
const DEFAULT_PROJECT_CHAT_OWNED_MESSAGE_IDS_REF = {
  current: PROJECT_CHAT_OWNED_MESSAGE_IDS,
};

const PROJECT_CHAT_ASSISTANT_RUNTIME_SCOPES = new WeakMap<
  object,
  string | null
>();

/**
 * Preserve shell hydration ownership when an acted-on durable descriptor is
 * projected under a replacement outcome id. Without the transfer, the next
 * project scope can remove the descriptor but leave its replacement behind.
 */
export function transferProjectChatHydrationOwnership(
  sourceIds: readonly string[],
  replacementId: string,
  ownedMessageIds: Set<string> = PROJECT_CHAT_OWNED_MESSAGE_IDS,
): void {
  if (!sourceIds.some((id) => ownedMessageIds.has(id))) return;
  for (const id of sourceIds) ownedMessageIds.delete(id);
  ownedMessageIds.add(replacementId);
}

export function projectChatModelReplyText(message: ThreadMessage): string {
  return message.content
    .filter(
      (part): part is Extract<(typeof message.content)[number], { type: "text" }> =>
        part.type === "text",
    )
    .map((part) => part.text.trim())
    .filter(Boolean)
    .join("\n\n");
}

export function projectChatModelReplyErrorText(
  error: unknown,
  locale: LocaleCode,
): string {
  if (typeof error === "string" && error.trim()) return error.trim();
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message.trim();
  }
  return {
    en: "The assistant could not complete this request.",
    ko: "어시스턴트가 이 요청을 완료하지 못했습니다.",
    ja: "アシスタントはこのリクエストを完了できませんでした。",
    zh: "助手无法完成此请求。",
    "zh-TW": "助手無法完成此請求。",
  }[locale];
}

function runtimeOwnsPersistedTurn(
  turn: ProjectChatThreadTurn,
  messages: readonly ThreadMessage[],
  locale: LocaleCode,
): boolean {
  if (turn.kind !== "user" && turn.kind !== "outcome") return false;
  if (turn.route !== "read-only" && turn.route !== "grounded-edit") {
    return false;
  }
  return messages.some((message) => {
    if (turn.kind === "user") {
      return (
        message.role === "user" &&
        projectChatModelReplyText(message) === turn.text
      );
    }
    if (message.role !== "assistant") return false;
    if (message.status?.type === "complete") {
      return projectChatModelReplyText(message) === turn.text;
    }
    if (message.status?.type === "incomplete") {
      return (
        projectChatModelReplyErrorText(message.status.error, locale) === turn.text
      );
    }
    return false;
  });
}

type AssistantThreadTurn = Extract<
  ProjectChatThreadTurn,
  { kind: "user" | "outcome" }
>;

function isAssistantThreadTurn(
  turn: ProjectChatThreadTurn,
): turn is AssistantThreadTurn & { presentation: "assistant-thread" } {
  return (
    (turn.kind === "user" ||
      (turn.kind === "outcome" && !turn.retry.retryable)) &&
    turn.presentation === "assistant-thread"
  );
}

/**
 * Hydrate the one inert, timestamp-ordered durable projection shared by both
 * Chat shells. Build mode has no assistant-ui thread surface, so its caller
 * omits `runtime`; edit mode supplies it so restored assistant turns are also
 * removed from the live rich-card runtime before projection.
 */
export function useProjectChatThreadHydration(
  runtime: ThreadRuntime | null = null,
  ownedMessageIdsRef: MutableRefObject<Set<string>> =
    DEFAULT_PROJECT_CHAT_OWNED_MESSAGE_IDS_REF,
  enabled = true,
): void {
  const { locale } = useStudioLocale();
  const currentProjectId = useStudioStore((state) => state.currentProjectId);
  const currentUserId = useStudioStore(
    (state) => state.session?.user?.id ?? null,
  );
  const runtimeRef = useRef(runtime);
  runtimeRef.current = runtime;
  const assistantRuntimeScopeRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return;
    const previousIds = new Set(ownedMessageIdsRef.current);
    const scope =
      currentUserId && currentProjectId
        ? { userId: currentUserId, projectId: currentProjectId }
        : null;
    const thread = scope ? readProjectChatThread(scope) : null;
    const activeRuntime = runtimeRef.current;
    const runtimeMessages = activeRuntime?.getState().messages ?? [];
    const markedTurns = thread?.turns.filter(isAssistantThreadTurn) ?? [];
    const assistantScopeKey = scope
      ? `${scope.userId}\u0000${scope.projectId}`
      : null;
    const previousAssistantScope = activeRuntime
      ? assistantRuntimeScopeRef.current !== undefined
        ? assistantRuntimeScopeRef.current
        : PROJECT_CHAT_ASSISTANT_RUNTIME_SCOPES.get(activeRuntime)
      : undefined;
    const resetAssistantRuntime =
      Boolean(activeRuntime) &&
      ((previousAssistantScope !== undefined &&
        previousAssistantScope !== assistantScopeKey) ||
        markedTurns.length > 0);
    if (activeRuntime && resetAssistantRuntime) {
      activeRuntime.reset([]);
    }
    const hydratedRuntimeMessages =
      activeRuntime && resetAssistantRuntime
        ? activeRuntime.getState().messages
        : runtimeMessages;
    assistantRuntimeScopeRef.current = assistantScopeKey;
    if (activeRuntime) {
      PROJECT_CHAT_ASSISTANT_RUNTIME_SCOPES.set(
        activeRuntime,
        assistantScopeKey,
      );
    }
    const hydratedMessages =
      thread?.turns
        .map((turn, sequence) => ({ turn, sequence }))
        .sort(
          (left, right) =>
            left.turn.timestamp - right.turn.timestamp ||
            left.sequence - right.sequence,
        )
        .map(({ turn }) => turn)
        .filter(
          (turn) =>
            !runtimeOwnsPersistedTurn(
              turn,
              hydratedRuntimeMessages,
              locale,
            ),
        )
        .map((turn) => {
          if (
            turn.kind === "generation-readiness" ||
            turn.kind === "template-proposal"
          ) {
            return turn;
          }
          return turn.kind === "user"
            ? {
                kind: "user" as const,
                id: turn.id,
                timestamp: turn.timestamp,
                text: turn.text,
              }
            : {
                kind: "project-chat-outcome" as const,
                id: turn.id,
                timestamp: turn.timestamp,
                route: turn.route,
                status: turn.status,
                text: turn.text,
                retry: turn.retry,
              };
        }) ?? [];
    const hydratedIds = new Set(hydratedMessages.map((message) => message.id));
    const currentMessages = useStudioStore
      .getState()
      .chatMessages.filter((message) => !previousIds.has(message.id));
    useStudioStore.setState({
      chatMessages: [
        ...hydratedMessages,
        ...currentMessages.filter((message) => !hydratedIds.has(message.id)),
      ],
    });
    ownedMessageIdsRef.current.clear();
    for (const id of hydratedIds) {
      ownedMessageIdsRef.current.add(id);
    }
  }, [currentProjectId, currentUserId, enabled, locale, ownedMessageIdsRef]);
}
